import { assertNever } from "@glideapps/ts-necessities";
import * as t from "io-ts";

import type { SubscriptionAddon } from "./billing-vnext/subscriptions";
import { AddonKind } from "./billing-vnext/subscriptions";
import { AppKind } from "./components/SerializedApp";

// NOTE: The entirety of this file is legacy and subject to removal sometime after the new billing system is merged.

export function displayDollars(n: number): string {
    const digits = Math.round(n) === n ? 0 : 2;
    return n.toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        style: "currency",
        currency: "USD",
    });
}

// NOTE: Used in functions
export enum SubscriptionType {
    Annual = "annual",
    Monthly = "monthly",
}

export enum PlanType {
    Free = "free",
    Basic = "basic",
    Pro = "pro",
    Enterprise = "enterprise",
}

export enum PlanPrivacy {
    Public = "public",
    Private = "private",
}

export function isChurning(prevPlan: PlanType | undefined, newPlan: PlanType | undefined): boolean {
    return prevPlan !== PlanType.Free && newPlan === PlanType.Free;
}

// NOTE: Used in functions
export enum AppPlanCode {
    Free = "free",
    BasicMonthly = "basic-monthly",
    BasicAnnual = "basic-annual",
    BasicPrivateMonthly = "basic-private-monthly",
    BasicPrivateAnnual = "basic-private-annual",
    ProMonthly = "pro-monthly",
    ProAnnual = "pro-annual",
    ProPrivateMonthly = "private-monthly",
    ProPrivateMonthlyPage = "private-monthly-page",
    ProPrivateAnnual = "private-annual",
    LegacyOrgPublicMonthly = "legacy-org-public-monthly",
    LegacyOrgInternalMonthly = "legacy-org-internal-monthly",
}

export function appKindFromAppPlanCode(plan: AppPlanCode): AppKind {
    switch (plan) {
        case AppPlanCode.ProPrivateMonthlyPage:
            return AppKind.Page;
        default:
            return AppKind.App;
    }
}

// TODO: Remove this once everyone is on the new billing system.
export function appPlanToSubscriptionAddonKind(plan: AppPlanCode): AddonKind | undefined {
    switch (plan) {
        case AppPlanCode.Free:
            return undefined;
        case AppPlanCode.LegacyOrgPublicMonthly:
            return AddonKind.V1OrgProPublic;
        case AppPlanCode.LegacyOrgInternalMonthly:
            return AddonKind.V1OrgProInternal;
        case AppPlanCode.BasicMonthly:
        case AppPlanCode.BasicAnnual:
            return AddonKind.V2BasicPublicApp;
        case AppPlanCode.BasicPrivateMonthly:
        case AppPlanCode.BasicPrivateAnnual:
            return AddonKind.V2BasicPrivateApp;
        case AppPlanCode.ProMonthly:
        case AppPlanCode.ProAnnual:
            return AddonKind.V2ProPublicApp;
        case AppPlanCode.ProPrivateMonthly:
        case AppPlanCode.ProPrivateAnnual:
            return AddonKind.V2ProPrivateApp;
        case AppPlanCode.ProPrivateMonthlyPage:
            return AddonKind.V2ProPrivatePage;
        default:
            assertNever(plan, "AppPlanCode is not handled in appPlanToSubscriptionPlanKind");
    }
}

export function addonToAppPlanCode(addon: SubscriptionAddon | undefined): AppPlanCode | undefined {
    if (addon === undefined) return undefined;
    const { kind, period } = addon;
    if (period === "annual") {
        switch (kind) {
            case AddonKind.V2BasicPublicApp:
                return AppPlanCode.BasicAnnual;
            case AddonKind.V2BasicPrivateApp:
                return AppPlanCode.BasicPrivateAnnual;
            case AddonKind.V2ProPublicApp:
                return AppPlanCode.ProAnnual;
            case AddonKind.V2ProPrivateApp:
                return AppPlanCode.ProPrivateAnnual;
            default:
                return undefined;
        }
    }
    switch (kind) {
        case AddonKind.V2BasicPublicApp:
            return AppPlanCode.BasicMonthly;
        case AddonKind.V2BasicPrivateApp:
            return AppPlanCode.BasicPrivateMonthly;
        case AddonKind.V2ProPublicApp:
            return AppPlanCode.ProMonthly;
        case AddonKind.V2ProPrivateApp:
            return AppPlanCode.ProPrivateMonthly;
        case AddonKind.V2ProPrivatePage:
            return AppPlanCode.ProPrivateMonthlyPage;
        default:
            return undefined;
    }
}

export function splitPlanCodeForAnalytics(
    code: AppPlanCode
): { plan: "basic" | "pro"; period: SubscriptionType; privacy: PlanPrivacy; kind: AppKind } | undefined {
    if (code === AppPlanCode.Free) return undefined;
    switch (code) {
        case AppPlanCode.BasicMonthly:
            return { plan: "basic", period: SubscriptionType.Monthly, privacy: PlanPrivacy.Public, kind: AppKind.App };
        case AppPlanCode.BasicAnnual:
            return { plan: "basic", period: SubscriptionType.Annual, privacy: PlanPrivacy.Public, kind: AppKind.App };
        case AppPlanCode.BasicPrivateMonthly:
            return { plan: "basic", period: SubscriptionType.Monthly, privacy: PlanPrivacy.Private, kind: AppKind.App };
        case AppPlanCode.BasicPrivateAnnual:
            return { plan: "basic", period: SubscriptionType.Annual, privacy: PlanPrivacy.Private, kind: AppKind.App };
        case AppPlanCode.ProMonthly:
            return { plan: "pro", period: SubscriptionType.Monthly, privacy: PlanPrivacy.Public, kind: AppKind.App };
        case AppPlanCode.ProPrivateMonthlyPage:
            return { plan: "pro", period: SubscriptionType.Monthly, privacy: PlanPrivacy.Private, kind: AppKind.Page };
        case AppPlanCode.ProAnnual:
            return { plan: "pro", period: SubscriptionType.Annual, privacy: PlanPrivacy.Public, kind: AppKind.App };
        case AppPlanCode.ProPrivateMonthly:
            return { plan: "pro", period: SubscriptionType.Monthly, privacy: PlanPrivacy.Private, kind: AppKind.App };
        case AppPlanCode.ProPrivateAnnual:
            return { plan: "pro", period: SubscriptionType.Annual, privacy: PlanPrivacy.Private, kind: AppKind.App };
        case AppPlanCode.LegacyOrgInternalMonthly:
        // fallthrough
        case AppPlanCode.LegacyOrgPublicMonthly:
            return undefined;
        default:
            return assertNever(code);
    }
}

// NOTE: Used in functions
export const allAppPlanCodes = [
    AppPlanCode.Free,
    AppPlanCode.BasicMonthly,
    AppPlanCode.BasicAnnual,
    AppPlanCode.BasicPrivateMonthly,
    AppPlanCode.BasicPrivateAnnual,
    AppPlanCode.ProMonthly,
    AppPlanCode.ProAnnual,
    AppPlanCode.ProPrivateMonthly,
    AppPlanCode.ProPrivateAnnual,
    AppPlanCode.ProPrivateMonthlyPage,
    AppPlanCode.LegacyOrgPublicMonthly,
    AppPlanCode.LegacyOrgInternalMonthly,
];

// NOTE: Used in functions
export function planIsMonthly(code: AppPlanCode): boolean {
    switch (code) {
        case AppPlanCode.ProAnnual:
        case AppPlanCode.BasicAnnual:
        case AppPlanCode.ProPrivateAnnual:
        case AppPlanCode.BasicPrivateAnnual:
            return false;
        case AppPlanCode.Free:
        case AppPlanCode.ProMonthly:
        case AppPlanCode.ProPrivateMonthly:
        case AppPlanCode.BasicMonthly:
        case AppPlanCode.BasicPrivateMonthly:
        case AppPlanCode.LegacyOrgPublicMonthly:
        case AppPlanCode.LegacyOrgInternalMonthly:
        case AppPlanCode.ProPrivateMonthlyPage:
            return true;
        default:
            assertNever(code);
    }
}

export function getPlanTypeName(planType: PlanType): string {
    switch (planType) {
        case PlanType.Free:
            return "Personal";
        case PlanType.Basic:
            return "Basic";
        case PlanType.Pro:
            return "Pro";
        case PlanType.Enterprise:
            return "Enterprise";
        default:
            assertNever(planType);
    }
}

export function getPlanPrivacyName(planPrivacy: PlanPrivacy): string {
    switch (planPrivacy) {
        case PlanPrivacy.Public:
            return "Public";
        case PlanPrivacy.Private:
            return "Private";
        default:
            assertNever(planPrivacy);
    }
}

export function getSubscriptionName(subscription: SubscriptionType): string {
    switch (subscription) {
        case SubscriptionType.Monthly:
            return "Monthly";
        case SubscriptionType.Annual:
            return "Yearly — Save 25%";
        default:
            assertNever(subscription);
    }
}

export function getPlanName(plan: AppPlanCode) {
    switch (plan) {
        case AppPlanCode.Free:
            return {
                short: "Personal",
                long: "Personal",
            };
        case AppPlanCode.BasicMonthly:
            return {
                short: "Basic",
                long: "Basic, Monthly",
            };
        case AppPlanCode.BasicAnnual:
            return {
                short: "Basic",
                long: "Basic, Annual",
            };
        case AppPlanCode.ProMonthly:
            return {
                short: "Pro",
                long: "Pro, Monthly",
            };
        case AppPlanCode.ProAnnual:
            return {
                short: "Pro",
                long: "Pro, Annual",
            };
        case AppPlanCode.ProPrivateMonthly:
            return {
                short: "Private Pro",
                long: "Private Pro, Monthly",
            };
        case AppPlanCode.ProPrivateMonthlyPage:
            return {
                short: "Pro",
                long: "Pro, Monthly",
            };
        case AppPlanCode.ProPrivateAnnual:
            return {
                short: "Private Pro",
                long: "Private Pro, Annual",
            };
        case AppPlanCode.BasicPrivateMonthly:
            return {
                short: "Private Basic",
                long: "Private Basic, Monthly",
            };
        case AppPlanCode.BasicPrivateAnnual:
            return {
                short: "Private Basic",
                long: "Private Basic, Annual",
            };
        case AppPlanCode.LegacyOrgPublicMonthly:
            return {
                short: "Pro",
                long: "Legacy Org Public, Monthly",
            };
        case AppPlanCode.LegacyOrgInternalMonthly:
            return {
                short: "Private",
                long: "Legacy Org Internal, Monthly",
            };
        default:
            assertNever(plan);
    }
}

type AllPlanStrings = "legacy" | AppPlanCode;

// NOTE: Used in functions
export const appPlans: Record<AllPlanStrings, AppPlanInfo> = {
    legacy: {
        code: AppPlanCode.ProMonthly,
        price: 29,
        title: "Legacy Pro app",
        units: "per month",
        isPrivate: false,
        isMonthly: true,
        isLegacy: true,
        numberOfUsers: 20,
    },
    [AppPlanCode.Free]: {
        code: AppPlanCode.Free,
        price: 0,
        title: "Personal app",
        units: "",
        isPrivate: false,
        isMonthly: false,
        isLegacy: false,
        numberOfUsers: Number.MAX_SAFE_INTEGER,
    },
    [AppPlanCode.BasicAnnual]: {
        code: AppPlanCode.BasicAnnual,
        price: 9,
        title: "Basic app",
        units: "per month",
        isPrivate: false,
        isMonthly: false,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.BasicMonthly]: {
        code: AppPlanCode.BasicMonthly,
        price: 12,
        title: "Basic app",
        units: "per month",
        isPrivate: false,
        isMonthly: true,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.BasicPrivateMonthly]: {
        code: AppPlanCode.BasicPrivateMonthly,
        price: 1,
        title: "Private Basic app",
        units: "per user, per month",
        isPrivate: true,
        isMonthly: true,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.ProPrivateMonthly]: {
        code: AppPlanCode.ProPrivateMonthly,
        price: 2,
        title: "Private Pro app",
        units: "per month",
        isPrivate: true,
        isMonthly: true,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.ProPrivateMonthlyPage]: {
        code: AppPlanCode.ProPrivateMonthlyPage,
        price: 2,
        title: "Pro page",
        units: "per month",
        isPrivate: true,
        isMonthly: true,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.BasicPrivateAnnual]: {
        code: AppPlanCode.BasicPrivateAnnual,
        price: 0.75,
        title: "Private Basic app",
        units: "per month",
        isPrivate: true,
        isMonthly: false,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.ProPrivateAnnual]: {
        code: AppPlanCode.ProPrivateAnnual,
        price: 1.5,
        title: "Private Pro app",
        units: "per month",
        isPrivate: true,
        isMonthly: false,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.ProAnnual]: {
        code: AppPlanCode.ProAnnual,
        price: 24,
        title: "Pro app",
        units: "per month",
        isPrivate: false,
        isMonthly: false,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.ProMonthly]: {
        code: AppPlanCode.ProMonthly,
        price: 32,
        title: "Pro app",
        units: "per month",
        isPrivate: false,
        isMonthly: true,
        isLegacy: false,
        numberOfUsers: 20,
    },
    [AppPlanCode.LegacyOrgInternalMonthly]: {
        code: AppPlanCode.LegacyOrgInternalMonthly,
        price: 8,
        title: "Legacy Org Internal",
        units: "per user monthly",
        isPrivate: true,
        isMonthly: true,
        isLegacy: true,
        numberOfUsers: 20,
    },
    [AppPlanCode.LegacyOrgPublicMonthly]: {
        code: AppPlanCode.LegacyOrgPublicMonthly,
        price: 29,
        title: "Legacy Org Public",
        units: "per month",
        isPrivate: false,
        isMonthly: true,
        isLegacy: true,
        numberOfUsers: 20,
    },
};

export const appPlanToMonthly: Record<AllPlanStrings, AllPlanStrings> = {
    legacy: AppPlanCode.ProMonthly,
    [AppPlanCode.Free]: AppPlanCode.Free,
    [AppPlanCode.BasicAnnual]: AppPlanCode.BasicMonthly,
    [AppPlanCode.BasicMonthly]: AppPlanCode.BasicMonthly,
    [AppPlanCode.BasicPrivateAnnual]: AppPlanCode.BasicPrivateMonthly,
    [AppPlanCode.BasicPrivateMonthly]: AppPlanCode.BasicPrivateMonthly,
    [AppPlanCode.ProPrivateAnnual]: AppPlanCode.ProPrivateMonthly,
    [AppPlanCode.ProPrivateMonthly]: AppPlanCode.ProPrivateMonthly,
    [AppPlanCode.ProPrivateMonthlyPage]: AppPlanCode.ProPrivateMonthlyPage,
    [AppPlanCode.ProAnnual]: AppPlanCode.ProMonthly,
    [AppPlanCode.ProMonthly]: AppPlanCode.ProMonthly,
    [AppPlanCode.LegacyOrgPublicMonthly]: AppPlanCode.LegacyOrgPublicMonthly,
    [AppPlanCode.LegacyOrgInternalMonthly]: AppPlanCode.LegacyOrgInternalMonthly,
};

export function planCodeToPlanType(code: AppPlanCode): PlanType | undefined {
    switch (code) {
        case AppPlanCode.Free:
            return PlanType.Free;
        case AppPlanCode.BasicAnnual:
        case AppPlanCode.BasicMonthly:
        case AppPlanCode.BasicPrivateMonthly:
        case AppPlanCode.BasicPrivateAnnual:
            return PlanType.Basic;
        case AppPlanCode.LegacyOrgInternalMonthly:
        case AppPlanCode.LegacyOrgPublicMonthly:
            return undefined;
        case AppPlanCode.ProMonthly:
        case AppPlanCode.ProPrivateMonthly:
        case AppPlanCode.ProPrivateMonthlyPage:
        case AppPlanCode.ProAnnual:
        case AppPlanCode.ProPrivateAnnual:
            return PlanType.Pro;
        default:
            return assertNever(code);
    }
}

export function planInfoToPlanType(info: AppPlanInfo): PlanType | undefined {
    if (info.isLegacy) return undefined;
    return planCodeToPlanType(info.code);
}

export function planCodeToSubscriptionType(code: AppPlanCode): SubscriptionType {
    switch (code) {
        case AppPlanCode.Free:
        case AppPlanCode.BasicMonthly:
        case AppPlanCode.BasicPrivateMonthly:
        case AppPlanCode.ProMonthly:
        case AppPlanCode.ProPrivateMonthly:
        case AppPlanCode.ProPrivateMonthlyPage:
        case AppPlanCode.LegacyOrgPublicMonthly:
        case AppPlanCode.LegacyOrgInternalMonthly:
            return SubscriptionType.Monthly;
        case AppPlanCode.BasicAnnual:
        case AppPlanCode.BasicPrivateAnnual:
        case AppPlanCode.ProAnnual:
        case AppPlanCode.ProPrivateAnnual:
            return SubscriptionType.Annual;
        default:
            return assertNever(code);
    }
}

export function planCodeToPlanPrivacy(code: AppPlanCode): PlanPrivacy {
    switch (code) {
        case AppPlanCode.ProPrivateAnnual:
        case AppPlanCode.ProPrivateMonthly:
        case AppPlanCode.BasicPrivateMonthly:
        case AppPlanCode.BasicPrivateAnnual:
        case AppPlanCode.ProPrivateMonthlyPage:
        case AppPlanCode.LegacyOrgInternalMonthly:
            return PlanPrivacy.Private;
        case AppPlanCode.Free:
        case AppPlanCode.BasicMonthly:
        case AppPlanCode.ProMonthly:
        case AppPlanCode.LegacyOrgPublicMonthly:
        case AppPlanCode.BasicAnnual:
        case AppPlanCode.ProAnnual:
            return PlanPrivacy.Public;
        default:
            return assertNever(code);
    }
}

export interface AppPlanInfo {
    code: AppPlanCode;
    price: number;
    title: string;
    units: string;
    isPrivate: boolean;
    isMonthly: boolean;
    isLegacy: boolean;
    numberOfUsers: number;
}

export function getAppPlanInfo(
    planType: PlanType,
    planPrivacy: PlanPrivacy,
    subscriptionType: SubscriptionType,
    appKind: AppKind,
    numberOfUsers: number = 20
): AppPlanInfo | undefined {
    const isPrivate = planPrivacy === PlanPrivacy.Private;
    const isAnnual = subscriptionType === SubscriptionType.Annual;

    let planInfo: AppPlanInfo = {
        ...appPlans[AppPlanCode.Free],
    };
    if (planType === PlanType.Free) {
        return planInfo;
    }

    // FIXME: Hack for now for pages enterprise plan
    if (planType === PlanType.Enterprise) return undefined;

    if (appKind === AppKind.Page) {
        if (!isAnnual && isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.ProPrivateMonthlyPage],
                numberOfUsers: 20,
            };
        } else {
            // Only monthly private supported for Pages
            return undefined;
        }
    } else if (planType === PlanType.Basic) {
        if (isAnnual && isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.BasicPrivateAnnual],
                numberOfUsers,
            };
        } else if (isAnnual && !isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.BasicAnnual],
                numberOfUsers: 1,
            };
        } else if (!isAnnual && isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.BasicPrivateMonthly],
                numberOfUsers: 20,
            };
        } else if (!isAnnual && !isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.BasicMonthly],
                numberOfUsers: 1,
            };
        }
    } else if (planType === PlanType.Pro) {
        if (isAnnual && isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.ProPrivateAnnual],
                numberOfUsers,
            };
        } else if (isAnnual && !isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.ProAnnual],
                numberOfUsers: 1,
            };
        } else if (!isAnnual && isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.ProPrivateMonthly],
                numberOfUsers: 20,
            };
        } else if (!isAnnual && !isPrivate) {
            planInfo = {
                ...appPlans[AppPlanCode.ProMonthly],
                numberOfUsers: 1,
            };
        }
    } else {
        assertNever(planType);
    }

    return planInfo;
}

interface PlanLineItems {
    privateUsers: string | undefined;
    costPerUser: string | undefined;
    planBaseCost: string;
    subTotal: string | undefined;
    dueToday: {
        displayAmount: string;
        isCredit: boolean;
    };
}

export function getLineItemsForPlan(
    plan: AppPlanCode,
    userCount: number | undefined,
    estimate: number | undefined
): PlanLineItems {
    const appPlan = appPlans[plan];
    userCount = userCount ?? 1;
    userCount = appPlan.isMonthly ? appPlan.numberOfUsers : userCount;

    const result: PlanLineItems = {
        privateUsers: undefined,
        costPerUser: undefined,
        planBaseCost: "",
        subTotal: "",
        dueToday: {
            displayAmount: "",
            isCredit: false,
        },
    };

    const fullPrice = getFullPriceForPlan(plan, userCount);
    result.planBaseCost = appPlan.isPrivate
        ? `${displayDollars(userCount * appPlan.price)} per month`
        : `${displayDollars(appPlan.price)} per month`;

    result.subTotal = !appPlan.isMonthly ? `${displayDollars(fullPrice)} yearly` : undefined;

    if (appPlan.isPrivate) {
        result.privateUsers = `${userCount ?? 20}`;
        result.costPerUser = appPlan.isMonthly ? `${displayDollars(2)}` : `${displayDollars(1.5)}`;
    }

    if (estimate === undefined) {
        result.dueToday = { displayAmount: "Calculating...", isCredit: false };
    } else if (estimate >= 0) {
        result.dueToday = { displayAmount: displayDollars(estimate), isCredit: false };
    } else {
        result.dueToday = { displayAmount: displayDollars(-estimate), isCredit: true };
    }
    return result;
}

export function getPrivatePerUserBillingExplanation(plan: AppPlanInfo, numUsers: number) {
    const correspondingMonthlyPlan = appPlans[appPlanToMonthly[plan.code]];
    if (plan.isMonthly) {
        return `You'll pay for 20 users at the beginning of each month. If you go over 20 users in a month, you'll be charged ${displayDollars(
            correspondingMonthlyPlan.price
        )} per additional user at the end of that month.`;
    } else {
        return `If you go over ${numUsers} users in a month, you'll be charged ${displayDollars(
            correspondingMonthlyPlan.price
        )} per additional user at the end of that month.`;
    }
}

// FIXME: Is this the best way to do this?
export const appPlanCodeCodec = t.union([
    t.literal(AppPlanCode.Free),
    t.literal(AppPlanCode.BasicMonthly),
    t.literal(AppPlanCode.BasicAnnual),
    t.literal(AppPlanCode.BasicPrivateMonthly),
    t.literal(AppPlanCode.BasicPrivateAnnual),
    t.literal(AppPlanCode.ProMonthly),
    t.literal(AppPlanCode.ProAnnual),
    t.literal(AppPlanCode.ProPrivateMonthly),
    t.literal(AppPlanCode.ProPrivateMonthlyPage),
    t.literal(AppPlanCode.ProPrivateAnnual),
    t.literal(AppPlanCode.LegacyOrgInternalMonthly),
    t.literal(AppPlanCode.LegacyOrgPublicMonthly),
]);

export function getFullPriceForPlan(plan: AppPlanCode, userCount: number | undefined): number {
    const appPlan = appPlans[plan];
    userCount = userCount ?? 1;

    if (appPlan.isPrivate) {
        if (appPlan.isMonthly) {
            return appPlan.price * userCount;
        } else {
            return appPlan.price * userCount * 12;
        }
    } else {
        if (appPlan.isMonthly) {
            return appPlan.price;
        } else {
            return appPlan.price * 12;
        }
    }
}
